/**
 * Global Cmd-K (Ctrl-K) keyboard shortcut for the command bar.
 *
 * Mounted at the application shell so the binding survives route
 * changes. Listens at the window level rather than the document level
 * because Radix Dialog manipulates focus on document.body and we want
 * the toggle to fire even when an input has focus.
 *
 * Esc closes the dialog through Radix's own keyboard handling; we do
 * not double-up on it here.
 *
 * Reference: spec section 7.5 and Claude Design Files README section
 * 5.14.
 */

import { useEffect } from 'react';
import { useCommandBar } from './command-bar';

export function useCommandBarShortcut(): void {
  const toggle = useCommandBar((s) => s.toggle);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      // Cmd-K on macOS, Ctrl-K everywhere else. `metaKey` is the
      // Command key on Mac; checking both keeps the shortcut working
      // on every platform without a runtime user-agent sniff.
      const isModifierK =
        (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k' && !event.shiftKey;
      if (isModifierK) {
        event.preventDefault();
        toggle();
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggle]);
}
